import type { JSX } from 'react';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Select } from '@artosera/ui';
import { TableauDefilant } from '../components/TableauDefilant.js';
import { lireDiagnosticIos, listerDefautsIos } from '../lib/api.js';
import type { CadreDiagnosticIos, DefautIos, FiltreDiagnosticsIos } from '../types.js';
import pageStyles from '../styles/page.module.css';
import tableStyles from '../styles/table.module.css';
import styles from './DiagnosticsIos.module.css';

const CLE = ['console', 'diagnostics-ios'] as const;
const FILTRES: readonly FiltreDiagnosticsIos[] = ['tous', 'plantage', 'blocage'];

/** Une ligne de pile, à la manière d'un rapport de plantage : rang, binaire, décalage. */
function ligneCadre(cadre: CadreDiagnosticIos, rang: number): string {
  const retrait = '  '.repeat(Math.min(cadre.profondeur, 12));
  return `${String(rang).padStart(3)}  ${retrait}${cadre.binaire} + ${cadre.decalage}`;
}

/**
 * `/diagnostics-ios` : les plantages et blocages de l'app iPhone et iPad,
 * regroupés par défaut (même version, même haut de pile). Lecture seule.
 */
export function DiagnosticsIos(): JSX.Element {
  const { t, i18n } = useTranslation('console');
  const [filtre, setFiltre] = useState<FiltreDiagnosticsIos>('tous');
  const [ouvert, setOuvert] = useState<string | null>(null);

  const requete = useQuery({
    queryKey: [...CLE, filtre],
    queryFn: () => listerDefautsIos(filtre),
  });

  const date = (iso: string): string =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso),
    );
  const defauts = requete.data ?? [];

  return (
    <section>
      <div className={pageStyles.entete}>
        <div>
          <h1>{t('diagnosticsIos.titre')}</h1>
          <p>{t('diagnosticsIos.texte')}</p>
        </div>
      </div>

      <div className={tableStyles.toolbar}>
        <div className={tableStyles.champFiltre}>
          <label htmlFor="diagnostics-ios-filtre">{t('diagnosticsIos.filtres.type')}</label>
          <Select
            id="diagnostics-ios-filtre"
            value={filtre}
            onValueChange={(valeur) => setFiltre(valeur as FiltreDiagnosticsIos)}
            options={FILTRES.map((valeur) => ({
              value: valeur,
              label: t(`diagnosticsIos.filtres.${valeur}`),
            }))}
          />
        </div>
      </div>

      {requete.isLoading ? (
        <p className={pageStyles.videEtat}>{t('diagnosticsIos.chargement')}</p>
      ) : requete.isError ? (
        <p role="alert" className={`${pageStyles.banniere} ${pageStyles.bannierePeril}`}>
          {t('diagnosticsIos.erreurChargement')}
        </p>
      ) : defauts.length === 0 ? (
        <p className={pageStyles.videEtat}>{t('diagnosticsIos.vide')}</p>
      ) : (
        <TableauDefilant libelle={t('diagnosticsIos.titre')}>
          <table className={tableStyles.table}>
            <thead>
              <tr>
                <th scope="col">{t('diagnosticsIos.colonnes.defaut')}</th>
                <th scope="col">{t('diagnosticsIos.colonnes.version')}</th>
                <th scope="col">{t('diagnosticsIos.colonnes.occurrences')}</th>
                <th scope="col">{t('diagnosticsIos.colonnes.appareils')}</th>
                <th scope="col">{t('diagnosticsIos.colonnes.dernier')}</th>
                <th scope="col" aria-label={t('diagnosticsIos.detail')} />
              </tr>
            </thead>
            <tbody>
              {defauts.map((defaut) => (
                <Fragment key={defaut.empreinte}>
                  <LigneDefaut
                    defaut={defaut}
                    date={date}
                    ouvert={ouvert === defaut.empreinte}
                    basculer={() =>
                      setOuvert((courant) =>
                        courant === defaut.empreinte ? null : defaut.empreinte,
                      )
                    }
                  />
                  {ouvert === defaut.empreinte ? (
                    <tr>
                      <td colSpan={6}>
                        <Pile empreinte={defaut.empreinte} />
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ))}
            </tbody>
          </table>
        </TableauDefilant>
      )}
    </section>
  );
}

function LigneDefaut({
  defaut,
  date,
  ouvert,
  basculer,
}: {
  defaut: DefautIos;
  date: (iso: string) => string;
  ouvert: boolean;
  basculer: () => void;
}): JSX.Element {
  const { t, i18n } = useTranslation('console');
  const secondes = (ms: number): string =>
    new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }).format(ms / 1000);
  const precision =
    defaut.plantage?.signal != null
      ? t('diagnosticsIos.signal', { signal: defaut.plantage.signal })
      : defaut.blocage
        ? t('diagnosticsIos.duree', { secondes: secondes(defaut.blocage.dureeMs) })
        : null;
  return (
    <tr>
      <td className={tableStyles.cellulePrincipale}>
        <Badge tone={defaut.type === 'plantage' ? 'sold' : 'reserved'}>
          {t(`diagnosticsIos.type.${defaut.type}`)}
        </Badge>{' '}
        {precision}
        <div className={styles.haut}>
          {defaut.haut.length === 0 ? (
            <span>{t('diagnosticsIos.sansPile')}</span>
          ) : (
            defaut.haut.map((cadre, rang) => (
              <span key={rang}>{`${cadre.binaire} + ${cadre.decalage}`}</span>
            ))
          )}
        </div>
      </td>
      <td>
        {t('diagnosticsIos.version', { version: defaut.app.version, build: defaut.app.build })}
        {defaut.app.testflight ? (
          <>
            {' '}
            <Badge tone="neutral">{t('diagnosticsIos.testflight')}</Badge>
          </>
        ) : null}
      </td>
      <td>{defaut.occurrences}</td>
      <td className={tableStyles.celluleMuette}>{defaut.appareils.join(', ')}</td>
      <td className={tableStyles.celluleMuette}>{date(defaut.dernierLe)}</td>
      <td className={tableStyles.celluleActions}>
        <Button variant="ghost" size="sm" aria-expanded={ouvert} onClick={basculer}>
          {ouvert ? t('diagnosticsIos.masquer') : t('diagnosticsIos.detail')}
        </Button>
      </td>
    </tr>
  );
}

function Pile({ empreinte }: { empreinte: string }): JSX.Element {
  const { t } = useTranslation('console');
  const requete = useQuery({
    queryKey: [...CLE, 'detail', empreinte],
    queryFn: () => lireDiagnosticIos(empreinte),
  });
  if (requete.isLoading) return <p>{t('diagnosticsIos.detailChargement')}</p>;
  if (requete.isError || !requete.data)
    return (
      <p role="alert" className={`${pageStyles.banniere} ${pageStyles.bannierePeril}`}>
        {t('diagnosticsIos.detailErreur')}
      </p>
    );
  const diagnostic = requete.data;
  return (
    <div className={styles.pile}>
      <p className={tableStyles.celluleMuette}>
        {t('diagnosticsIos.contexte', {
          appareil: diagnostic.appareil,
          systeme: diagnostic.systeme,
          architecture: diagnostic.architecture,
        })}
      </p>
      {diagnostic.plantage?.raisonFin ? (
        <p>{t('diagnosticsIos.raisonFin', { raison: diagnostic.plantage.raisonFin })}</p>
      ) : null}
      {diagnostic.plantage?.nomException ? (
        <p>{t('diagnosticsIos.exception', { nom: diagnostic.plantage.nomException })}</p>
      ) : null}
      {diagnostic.fils.map((fil, rang) => (
        <div key={rang}>
          <p className={styles.filTitre}>
            {fil.attribue
              ? t('diagnosticsIos.filAttribue')
              : t('diagnosticsIos.fil', { rang: rang + 1 })}
          </p>
          <pre className={styles.fil}>{fil.cadres.map(ligneCadre).join('\n')}</pre>
        </div>
      ))}
    </div>
  );
}
